import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { io, Socket } from "socket.io-client";
import { devLog } from "../lib/logger";

interface SocketContextValue {
  socket: Socket | null;
  isConnected: boolean;
}

const SocketContext = createContext<SocketContextValue>({
  socket: null,
  isConnected: false,
});

interface SocketProviderProps {
  children: ReactNode;
  userId: string;
  userName: string;
  userAvatar?: string;
}

export function SocketProvider({ children, userId, userName, userAvatar }: SocketProviderProps) {
  const [socket, setSocket] = useState<Socket | null>(null);
  const [isConnected, setIsConnected] = useState(false);

  useEffect(() => {
    // Initialize Socket.IO connection
    // Connect to the root domain, not /api, since Socket.IO uses /socket.io/ path
    const apiUrl = import.meta.env.VITE_API_URL || "http://localhost:3000";
    const socketUrl = apiUrl.replace(/\/api$/, ''); // Remove /api suffix if present

    devLog.info("[Socket.IO] Connecting to:", socketUrl);

    // SECURITY: Authentication happens via httpOnly cookies
    // withCredentials: true ensures cookies are sent with the connection
    const socketInstance = io(socketUrl, {
      withCredentials: true, // Send httpOnly cookies for authentication
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 5000,
      reconnectionAttempts: 5,
    });

    socketInstance.on("connect", () => {
      devLog.info("[Socket.IO] Connected");
      setIsConnected(true);
    });

    socketInstance.on("disconnect", () => {
      devLog.info("[Socket.IO] Disconnected");
      setIsConnected(false);
    });

    socketInstance.on("connect_error", (error) => {
      devLog.error("[Socket.IO] Connection error:", error);
    });

    // Publishing the just-created Socket.IO instance to context is the
    // whole point of this effect; set-state-in-effect flags the pattern
    // but there is no other place to hand the instance to consumers.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSocket(socketInstance);

    return () => {
      socketInstance.close();
    };
  }, [userId, userName, userAvatar]);

  return (
    <SocketContext.Provider value={{ socket, isConnected }}>
      {children}
    </SocketContext.Provider>
  );
}

export function useSocket() {
  const context = useContext(SocketContext);
  if (!context) {
    throw new Error("useSocket must be used within a SocketProvider");
  }
  return context;
}
